import { Divider } from '@mui/material';
import {
  GridColumnVisibilityModel,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { useEffect, useMemo, useRef } from 'react';

import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportDefaultProps,
  AnalyticsReportRoleScope,
  AnalyticsReportTypes,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetSelector,
  ReportViewLayout,
  SavedReportSettingsSelector,
  useReportDataset,
  useSavedReportSettingsConfig,
} from '~/scenes/PortalModules/Analytics/common';
import {
  WorkflowReportCellRenderToggles,
  useWorkflowReportCellRenderToggles,
} from '~/scenes/PortalModules/Analytics/People/WorkflowReport/Common/components/WorkflowReportCellRenderToggles';

import { WorkflowReportConfig } from '../../types';

type WorkflowReportBaseProps<TData, TRow extends GridValidRowModel> = {
  config: WorkflowReportConfig<TData, TRow>;
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
};

export const WorkflowReportBase = <TData, TRow extends GridValidRowModel>(
  props: WorkflowReportBaseProps<TData, TRow>,
) => {
  const { config, initialColumnVisibilityModel } = props;

  const reportHeading = useMemo(() => {
    return !!config.reportSubtitle
      ? `${config.reportTitle} - ${config.reportSubtitle}`
      : config.reportTitle;
  }, [config]);

  const apiRef = useGridApiRef();

  const defaultReportSettings: AnalyticsReportDefaultProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
    },
  };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSettings,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        roles: [
          AnalyticsReportRoleScope.Manager,
          AnalyticsReportRoleScope.AltManager,
        ],
      },
    },
  });

  const reportSettingsConfig = useSavedReportSettingsConfig({
    reportType: config.reportType,
    reportDataset: reportDatasetProps,
    apiRef,
  });

  const { data, isLoading, failedToLoad } = config.dataFetchHook(
    reportDatasetProps.reportSelection,
  );

  const cellRenderProps = useWorkflowReportCellRenderToggles();

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    apiRef.current?.resetRowHeights();
  }, [apiRef, cellRenderProps.cellRenderState]);

  const { columns, rows } = config.useReportHook({
    data,
    cellRenderState: cellRenderProps.cellRenderState,
  });

  const rowsWithUniqueId = rows.map((row, index) => ({
    ...row,
    uniqueRowId: `${row.employeeId}-${index}`,
  }));

  return (
    <ReportViewLayout
      heading={reportHeading}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoading}
                failedToLoad={failedToLoad}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              {(config.visibleToggles === undefined ||
                config.visibleToggles.length > 0) && (
                <>
                  <WorkflowReportCellRenderToggles
                    {...cellRenderProps}
                    visibleToggles={config.visibleToggles}
                    sx={{ ml: 1 }}
                  />
                  <Divider orientation={'vertical'} sx={{ mr: 1 }} />
                </>
              )}
              <SavedReportSettingsSelector
                {...reportSettingsConfig}
                apiRef={apiRef}
                reportDataset={reportDatasetProps}
                isLoading={isLoading}
              />
              <Divider orientation={'vertical'} />
              <ReportColumnsVisibility apiRef={apiRef} isLoading={isLoading} />
              <ReportDataExport
                title={reportHeading}
                apiRef={apiRef}
                isLoading={isLoading}
              />
            </>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={rowsWithUniqueId}
        loading={isLoading}
        density={'compact'}
        getRowId={(row) => row.uniqueRowId}
        getRowHeight={() => 'auto'}
        pinnedColumns={{
          left: ['employeeId', 'firstName', 'lastName'],
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            paddingTop: 1,
            paddingBottom: 1,
          },
        }}
        initialState={
          initialColumnVisibilityModel
            ? {
                columns: {
                  columnVisibilityModel: initialColumnVisibilityModel,
                },
              }
            : undefined
        }
      />
    </ReportViewLayout>
  );
};
